import { SuperInput } from "@components:SuperInput/SuperInput";
import { Box, MenuItem, Typography } from "@mui/material";
import { get } from "lodash";
import { memo, useEffect } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { useFieldError, useFieldName, useSchemaForm } from "../context";
import { buildDefault } from "../defaults";
import { OneOfNode } from "../types";
import { FieldLabel } from "./FieldLabel";
import { ObjectField } from "./ObjectField";

/*
 * A union of objects differentiated by a discriminator (e.g. the LLM `provider`).
 * A select for the variant, then the fields of the selected variant.
 * Without a value, it writes its default variant, so the form state matches the select.
 */
export const OneOfField = memo(({ node }: { node: OneOfNode }) => {
  const { t } = useTranslation();
  const { valueCache } = useSchemaForm();
  const { control, getValues, setValue } = useFormContext();
  const name = useFieldName(node.path);
  const value = useWatch({ control, name });
  const error = useFieldError(node.path);
  const missing = value === undefined || value === null;

  // Checks the form itself: the record can load between the render and the effect
  useEffect(() => {
    if (missing && node.options.length > 0 && getValues(name) == null) {
      setValue(name, buildDefault(node, false));
    }
  }, [missing, name, node, getValues, setValue]);

  if (node.options.length === 0) {
    return (
      <Box sx={{ mb: 2 }}>
        <FieldLabel
          title={t("schemaForm.providerTitle", { name: node.title })}
          description={t("schemaForm.noProviderOptions")}
          required={node.required}
        />
        <Typography color="error" variant="body2">
          {t("schemaForm.noValidProviderOptions")}
        </Typography>
      </Box>
    );
  }

  const current =
    get(value, node.discriminator) ??
    node.defaultOption ??
    node.options[0].value;
  const selected =
    node.options.find((option) => option.value === current) ?? node.options[0];

  const select = (next: string) => {
    const option = node.options.find((item) => item.value === next);
    if (!option) return;

    if (value && typeof value === "object") {
      valueCache.current[`${name}.${current}`] = value;
    }

    const cached = valueCache.current[`${name}.${next}`];
    setValue(
      name,
      cached ?? {
        [node.discriminator]: next,
        ...(buildDefault(option.node, false) as object),
      },
      { shouldDirty: true },
    );
  };

  return (
    <Box sx={{ mb: 2 }}>
      <FieldLabel
        title={t("schemaForm.providerTitle", { name: node.title })}
        description={t("schemaForm.selectProviderType")}
        required={node.required}
      />

      <SuperInput.Select
        value={current}
        onChange={(e) => select(String(e.target.value))}
        inputProps={{ "aria-label": node.title }}
        errorText={error}
        sx={{ width: "100%", "& .SuperInput__Select": { width: "100%" } }}
      >
        {node.options.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </SuperInput.Select>

      <Box sx={{ mt: 2 }}>
        <ObjectField key={selected.value} node={selected.node} bare />
      </Box>
    </Box>
  );
});

OneOfField.displayName = "OneOfField";
